import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { format, isToday, isTomorrow, parseISO } from 'date-fns';
import { FaCalendarAlt, FaCar, FaChevronLeft, FaSpinner, FaWrench } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { useVehicle } from '../../contexts/VehicleContext';
import { bookingService, publicGarageService } from '../../services/apiService';

const PERIOD_LABELS = {
  morning: 'Morning',
  afternoon: 'Afternoon',
  evening: 'Evening',
};

function toLocalDateStr(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function isValidObjectId(value) {
  return /^[a-f\d]{24}$/i.test(String(value || '').trim());
}

function formatDayChipLabel(dateStr) {
  const d = parseISO(dateStr);
  if (isToday(d)) return 'Today';
  if (isTomorrow(d)) return 'Tom';
  return format(d, 'EEE');
}

function buildNext7DayFallback() {
  const out = [];
  const today = new Date();
  for (let i = 0; i < 7; i += 1) {
    const d = new Date(today);
    d.setDate(d.getDate() + i);
    const date = toLocalDateStr(d);
    out.push({
      date,
      label: d.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' }),
      hasAvailability: true,
      fullyBooked: false,
    });
  }
  return out;
}

function StepPill({ active, done, children }) {
  return (
    <span
      className={`inline-flex min-h-[2rem] flex-1 items-center justify-center rounded-full px-2 py-1 text-center text-[11px] font-semibold leading-tight sm:flex-none sm:px-4 sm:text-xs ${
        active
          ? 'bg-red-600 text-white'
          : done
            ? 'bg-red-100 text-red-800'
            : 'bg-gray-200 text-gray-600'
      }`}
    >
      {children}
    </span>
  );
}

function SummaryRow({ label, value }) {
  return (
    <div className="flex flex-col gap-0.5 py-2.5 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
      <dt className="shrink-0 text-xs font-medium text-gray-500">{label}</dt>
      <dd className="text-sm font-medium text-gray-900 break-words sm:max-w-[60%] sm:text-right">{value}</dd>
    </div>
  );
}

export default function GarageBooking() {
  const { garageId } = useParams();
  const navigate = useNavigate();
  const { vehicles, loading: vehiclesLoading } = useVehicle();

  const [step, setStep] = useState(1);
  const [garage, setGarage] = useState(null);
  const [calendar, setCalendar] = useState([]);
  const [slotsData, setSlotsData] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [vehicleId, setVehicleId] = useState('');
  const [serviceType, setServiceType] = useState('');
  const [problemDescription, setProblemDescription] = useState('');
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [pickupDrop, setPickupDrop] = useState(false);
  const [advancePaymentId, setAdvancePaymentId] = useState('');

  const vehicleList = useMemo(
    () => (Array.isArray(vehicles) ? vehicles : []),
    [vehicles]
  );

  const serviceOptions = useMemo(() => {
    const fromGarage = garage?.services || [];
    const names = fromGarage.map((s) => s.name).filter(Boolean);
    return names.length ? names : ['General Service', 'Periodic Service', 'Repair'];
  }, [garage]);

  useEffect(() => {
    if (!vehicleList.length) return;
    if (!vehicleId) {
      const def = vehicleList.find((v) => v.isDefault) || vehicleList[0];
      setVehicleId(String(def._id || def.id || ''));
    }
  }, [vehicleList, vehicleId]);

  useEffect(() => {
    if (serviceOptions.length && !serviceType) {
      setServiceType(serviceOptions[0]);
    }
  }, [serviceOptions, serviceType]);

  const loadProfile = useCallback(async () => {
    setLoadingProfile(true);
    try {
      const data = await publicGarageService.getProfile(garageId);
      setGarage(data.garage);
      const cal = await publicGarageService.getSlotCalendar(garageId, 7);
      setCalendar(cal.calendar?.length ? cal.calendar : buildNext7DayFallback());
    } catch (err) {
      toast.error(err.message || 'Failed to load garage');
      setCalendar(buildNext7DayFallback());
    } finally {
      setLoadingProfile(false);
    }
  }, [garageId]);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  const loadSlots = useCallback(
    async (date, { preserveTime = false } = {}) => {
      if (!date) return;
      setLoadingSlots(true);
      if (!preserveTime) setSelectedTime('');
      try {
        const data = await publicGarageService.getSlots(garageId, date);
        setSlotsData(data);
      } catch (err) {
        toast.error(err.message || 'Could not load slots');
        setSlotsData(null);
      } finally {
        setLoadingSlots(false);
      }
    },
    [garageId]
  );

  useEffect(() => {
    if (selectedDate) loadSlots(selectedDate, { preserveTime: step === 2 });
  }, [selectedDate, loadSlots, step]);

  useEffect(() => {
    const firstOpen = calendar.find((d) => d.hasAvailability && !d.fullyBooked);
    if (firstOpen && !selectedDate) setSelectedDate(firstOpen.date);
  }, [calendar, selectedDate]);

  const selectedVehicle = vehicleList.find((v) => String(v._id || v.id) === String(vehicleId));

  const canContinueStep1 =
    vehicleId &&
    serviceType &&
    problemDescription.trim().length >= 10 &&
    selectedDate &&
    selectedTime;

  const advanceRequired = Boolean(garage?.advanceRequired && garage?.advanceAmount > 0);
  const canConfirm = !advanceRequired || Boolean(advancePaymentId);

  const selectedSlotLabel = useMemo(() => {
    if (!selectedDate || !selectedTime) return null;
    const day = calendar.find((d) => d.date === selectedDate);
    const dateLabel = day?.label || format(parseISO(selectedDate), 'EEE, d MMM');
    const slotMatch = (slotsData?.slots || []).find((s) => s.time === selectedTime);
    const timeLabel = slotMatch?.label || selectedTime;
    return `${dateLabel} · ${timeLabel}`;
  }, [calendar, selectedDate, selectedTime, slotsData]);

  const handleContinue = () => {
    if (!canContinueStep1) {
      toast.warn('Complete vehicle, service, description (10+ chars), date and time.');
      return;
    }
    setStep(2);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handlePayAdvance = () => {
    setAdvancePaymentId(`adv_stub_${Date.now()}`);
    toast.success('Advance payment recorded (demo).');
  };

  const handleConfirm = async () => {
    if (!canConfirm) {
      toast.warn('Advance payment is required.');
      return;
    }
    if (!isValidObjectId(garageId)) {
      toast.error('Invalid garage. Go back and try again.');
      return;
    }
    if (!isValidObjectId(vehicleId)) {
      toast.error('Select a valid vehicle before confirming.');
      return;
    }
    if (!selectedDate || !/^\d{4}-\d{2}-\d{2}$/.test(selectedDate)) {
      toast.error('Pick a valid date.');
      return;
    }
    if (!selectedTime) {
      toast.error('Pick a time slot before confirming.');
      setStep(1);
      return;
    }
    if (problemDescription.trim().length < 10) {
      toast.error('Problem description must be at least 10 characters.');
      setStep(1);
      return;
    }

    setSubmitting(true);
    try {
      const result = await bookingService.create({
        garageId,
        vehicleId: String(vehicleId).trim(),
        serviceType: serviceType.trim(),
        slotDate: selectedDate,
        slotTime: selectedTime,
        problemDescription: problemDescription.trim(),
        pickupDrop,
        advancePaymentId: advancePaymentId || undefined,
      });
      toast.success('Booking confirmed! WhatsApp confirmation sent.');
      const bookingId = result.bookingId;
      if (bookingId) {
        navigate(`/service/booking/status/${bookingId}`);
      } else {
        const jobId = result.jobId || result.jobCardId;
        navigate(`/service/job/${jobId}`);
      }
    } catch (err) {
      if (err.code === 'SLOT_TAKEN' || /slot just got taken/i.test(err.message || '')) {
        toast.error('Slot just got taken — pick another.');
        setStep(1);
        setSelectedTime('');
        if (selectedDate) loadSlots(selectedDate);
      } else if (err.status === 402 || /advance payment/i.test(err.message || '')) {
        toast.error(err.message || 'Advance payment is required.');
      } else if (/vehicle/i.test(err.message || '')) {
        toast.error(err.message);
        setStep(1);
      } else {
        toast.error(err.message || 'Booking failed');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const renderSlotGrid = (periodKey) => {
    const list = slotsData?.periods?.[periodKey] || [];
    if (!list.length) return null;
    return (
      <div key={periodKey} className="mt-4 first:mt-0">
        <h3 className="text-[10px] font-bold uppercase tracking-widest text-gray-500 sm:text-xs">
          {PERIOD_LABELS[periodKey]}
        </h3>
        <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-3">
          {list.map((slot) => {
            const active = selectedTime === slot.time;
            const disabled = !slot.available;
            return (
              <button
                key={slot.time}
                type="button"
                disabled={disabled}
                onClick={() => setSelectedTime(slot.time)}
                className={`min-h-[3.25rem] rounded-lg border px-2 py-2.5 text-left text-sm transition active:scale-[0.98] ${
                  disabled
                    ? 'cursor-not-allowed border-gray-200 bg-gray-100 text-gray-400'
                    : active
                      ? 'border-red-600 bg-red-50 text-red-800 ring-1 ring-red-600'
                      : 'border-gray-200 bg-white text-gray-800 hover:border-red-300'
                }`}
              >
                <span className="block truncate font-semibold">{slot.label}</span>
                <span className="mt-0.5 block text-[10px] text-gray-500 sm:text-[11px]">{slot.statusLabel}</span>
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  if (loadingProfile) {
    return (
      <div className="checkout-light-page flex min-h-[50vh] items-center justify-center px-4">
        <FaSpinner className="animate-spin text-2xl text-red-600" />
      </div>
    );
  }

  return (
    <div className="checkout-light-page w-full bg-slate-50 pt-3 pb-36 sm:px-6 sm:pt-6 xl:pb-12 lg:px-8">
      <div className="mx-auto w-full max-w-6xl px-4 sm:px-0">
        <Link
          to={`/garages/${garageId}`}
          className="mb-3 inline-flex items-center gap-1 text-sm font-medium text-red-600 hover:underline sm:mb-4"
        >
          <FaChevronLeft className="text-xs" />
          Back to garage
        </Link>

        <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between sm:gap-4">
          <div className="min-w-0">
            <h1 className="text-xl font-bold text-gray-900 break-words sm:text-2xl lg:text-3xl">
              Book a service slot
            </h1>
            <p className="mt-1 text-xs text-gray-600 break-words sm:text-sm">
              {garage?.displayName || 'Partner garage'}
            </p>
          </div>
          <div className="flex w-full gap-2 sm:w-auto sm:shrink-0">
            <StepPill active={step === 1} done={step > 1}>
              1. Pick slot
            </StepPill>
            <StepPill active={step === 2} done={false}>
              2. Confirm
            </StepPill>
          </div>
        </div>

        {step === 1 && (
          <div className="mt-4 space-y-4 sm:mt-6 xl:grid xl:grid-cols-12 xl:gap-6 xl:space-y-0">
            {/* Form — vehicle, service, description */}
            <div className="space-y-4 rounded-xl border border-gray-200 bg-white p-4 shadow-sm sm:space-y-5 sm:p-5 xl:col-span-5">
              <section>
                <h2 className="text-xs font-bold uppercase tracking-widest text-gray-500 sm:text-sm sm:normal-case sm:tracking-normal sm:text-gray-900">
                  Your vehicle
                </h2>
                {vehiclesLoading ? (
                  <p className="mt-2 text-sm text-gray-500">Loading vehicles…</p>
                ) : vehicleList.length === 0 ? (
                  <p className="mt-2 text-sm text-gray-600">
                    <Link to="/account" className="font-medium text-red-600 hover:underline">
                      Add a vehicle
                    </Link>{' '}
                    in your account first.
                  </p>
                ) : (
                  <div className="mt-2 flex flex-col gap-2 sm:flex-row sm:flex-wrap">
                    {vehicleList.map((v) => {
                      const id = String(v._id || v.id);
                      const active = vehicleId === id;
                      const label = v.nickname || `${v.make} ${v.model}`;
                      return (
                        <button
                          key={id}
                          type="button"
                          onClick={() => setVehicleId(id)}
                          className={`inline-flex min-h-[2.75rem] w-full items-center gap-2 rounded-xl border px-3 py-2 text-left text-sm font-medium sm:w-auto sm:rounded-full sm:py-1.5 ${
                            active
                              ? 'border-red-600 bg-red-50 text-red-800'
                              : 'border-gray-300 bg-white text-gray-700'
                          }`}
                        >
                          <FaCar className="shrink-0 text-xs" />
                          <span className="truncate">{label}</span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </section>

              <section>
                <label htmlFor="service-type" className="text-xs font-bold uppercase tracking-widest text-gray-500 sm:text-sm sm:normal-case sm:tracking-normal sm:text-gray-900">
                  Service type
                </label>
                <select
                  id="service-type"
                  value={serviceType}
                  onChange={(e) => setServiceType(e.target.value)}
                  className="mt-2 w-full min-h-[2.75rem] rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-900"
                >
                  {serviceOptions.map((name) => (
                    <option key={name} value={name}>
                      {name}
                    </option>
                  ))}
                </select>
              </section>

              <section>
                <label htmlFor="problem-desc" className="text-xs font-bold uppercase tracking-widest text-gray-500 sm:text-sm sm:normal-case sm:tracking-normal sm:text-gray-900">
                  Problem description
                </label>
                <textarea
                  id="problem-desc"
                  value={problemDescription}
                  onChange={(e) => setProblemDescription(e.target.value)}
                  rows={4}
                  placeholder="Describe the issue (min 10 characters)…"
                  className="mt-2 w-full resize-y rounded-lg border border-gray-300 px-3 py-2.5 text-sm text-gray-900 sm:rows-3"
                />
                <p className={`mt-1 text-xs ${problemDescription.trim().length >= 10 ? 'text-emerald-600' : 'text-amber-600'}`}>
                  {problemDescription.trim().length}/10 min characters
                  {problemDescription.trim().length < 10 && (
                    <span className="text-gray-500"> — required to continue</span>
                  )}
                </p>
              </section>

              {garage?.supportsPickupDrop && (
                <label className="flex min-h-[2.75rem] cursor-pointer items-center gap-3 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={pickupDrop}
                    onChange={(e) => setPickupDrop(e.target.checked)}
                    className="h-4 w-4 shrink-0 rounded border-gray-300 text-red-600"
                  />
                  Request pickup & drop
                </label>
              )}

              {/* Desktop continue */}
              <button
                type="button"
                onClick={handleContinue}
                disabled={!canContinueStep1}
                className="hidden w-full rounded-xl bg-red-600 py-3 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-50 xl:block"
              >
                Continue
              </button>
            </div>

            {/* Calendar + slots */}
            <div className="space-y-4 rounded-xl border border-gray-200 bg-white p-4 shadow-sm sm:p-5 xl:col-span-7">
              <section>
                <h2 className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-gray-500 sm:text-sm sm:normal-case sm:tracking-normal sm:text-gray-900">
                  <FaCalendarAlt className="text-red-600" />
                  Choose date
                </h2>
                {selectedDate && (
                  <p className="mt-1 text-xs text-gray-500 sm:text-sm">
                    Selected:{' '}
                    <span className="font-medium text-gray-900">
                      {format(parseISO(selectedDate), 'EEEE, d MMMM yyyy')}
                    </span>
                  </p>
                )}
                {calendar.length === 0 ? (
                  <p className="mt-3 rounded-lg border border-gray-200 bg-gray-50 px-3 py-6 text-center text-sm text-gray-500">
                    No available dates in the next 7 days.
                  </p>
                ) : (
                  <div className="mt-2.5 grid grid-cols-7 gap-1 sm:gap-1.5">
                    {calendar.map((day) => {
                      const disabled = !day.hasAvailability || day.fullyBooked;
                      const active = selectedDate === day.date;
                      const dateObj = parseISO(day.date);
                      return (
                        <button
                          key={day.date}
                          type="button"
                          disabled={disabled}
                          onClick={() => setSelectedDate(day.date)}
                          title={disabled ? 'Fully booked' : format(dateObj, 'EEEE, d MMMM yyyy')}
                          className={`flex flex-col items-center rounded-lg border py-1.5 text-center transition sm:py-2 ${
                            disabled
                              ? 'cursor-not-allowed border-gray-100 bg-gray-50 opacity-60'
                              : active
                                ? 'border-red-600 bg-red-50'
                                : 'border-gray-200 bg-white hover:border-red-200 hover:bg-red-50/40'
                          }`}
                        >
                          <span
                            className={`truncate px-0.5 text-[9px] font-medium leading-tight sm:text-[10px] ${
                              disabled ? 'text-gray-400' : active ? 'text-red-700' : 'text-gray-500'
                            }`}
                          >
                            {formatDayChipLabel(day.date)}
                          </span>
                          <span
                            className={`mt-1 flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold sm:h-8 sm:w-8 sm:text-sm ${
                              disabled
                                ? 'text-gray-400'
                                : active
                                  ? 'bg-red-600 text-white shadow-sm'
                                  : 'text-gray-900'
                            }`}
                          >
                            {format(dateObj, 'd')}
                          </span>
                          <span
                            className={`mt-0.5 text-[9px] sm:text-[10px] ${
                              disabled ? 'text-gray-400' : active ? 'text-red-600' : 'text-gray-400'
                            }`}
                          >
                            {format(dateObj, 'MMM')}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )}
                <p className="mt-2 text-[11px] text-gray-400 sm:text-xs">
                  Next 7 days only
                </p>
              </section>

              <section>
                <h2 className="text-xs font-bold uppercase tracking-widest text-gray-500 sm:text-sm sm:normal-case sm:tracking-normal sm:text-gray-900">
                  Time slots
                </h2>
                {loadingSlots ? (
                  <div className="mt-4 flex items-center gap-2 text-sm text-gray-500">
                    <FaSpinner className="animate-spin" /> Loading slots…
                  </div>
                ) : slotsData?.fullyBooked ? (
                  <p className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-3 text-sm text-amber-800">
                    This day is fully booked. Pick another date.
                  </p>
                ) : (
                  <div className="mt-3">
                    {['morning', 'afternoon', 'evening'].map(renderSlotGrid)}
                    {!slotsData?.slots?.length && selectedDate && (
                      <p className="mt-3 text-sm text-gray-500">No slots for this date.</p>
                    )}
                  </div>
                )}
              </section>

              {selectedSlotLabel && (
                <div className="rounded-lg border border-red-100 bg-red-50 px-3 py-2 text-sm text-red-800 xl:hidden">
                  <span className="font-semibold">Slot:</span> {selectedSlotLabel}
                </div>
              )}
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="mx-auto mt-4 w-full max-w-lg sm:mt-6">
            <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
              <div className="border-b border-gray-100 bg-gray-50 px-4 py-4 sm:px-5">
                <h2 className="text-base font-bold text-gray-900 sm:text-lg">Confirm booking</h2>
                <p className="mt-0.5 text-xs text-gray-500 sm:text-sm">Review your details before confirming.</p>
              </div>

              {selectedSlotLabel && (
                <div className="flex items-center gap-2 border-b border-red-100 bg-red-50 px-4 py-3 sm:px-5">
                  <FaCalendarAlt className="shrink-0 text-sm text-red-600" />
                  <span className="text-sm font-semibold text-red-800">{selectedSlotLabel}</span>
                </div>
              )}

              <dl className="divide-y divide-gray-100 px-4 sm:px-5">
                <SummaryRow label="Garage" value={garage?.displayName || '—'} />
                <SummaryRow
                  label="Vehicle"
                  value={
                    selectedVehicle
                      ? `${selectedVehicle.make} ${selectedVehicle.model} (${selectedVehicle.registrationNumber || '—'})`
                      : '—'
                  }
                />
                <SummaryRow label="Service" value={serviceType} />
                <SummaryRow
                  label="Issue"
                  value={<span className="block max-w-[14rem] text-right sm:max-w-xs">{problemDescription}</span>}
                />
                {pickupDrop && (
                  <SummaryRow
                    label="Extras"
                    value={
                      <span className="inline-flex items-center justify-end gap-1">
                        <FaWrench className="text-gray-400" />
                        Pickup & drop
                      </span>
                    }
                  />
                )}
              </dl>

              {advanceRequired && (
                <div className="mx-4 mb-4 rounded-lg border border-amber-200 bg-amber-50 p-3 sm:mx-5">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <p className="text-xs font-medium text-amber-900">Advance payment required</p>
                      <p className="text-lg font-bold text-amber-950">
                        ₹{garage.advanceAmount.toLocaleString('en-IN')}
                      </p>
                    </div>
                    {!advancePaymentId ? (
                      <button
                        type="button"
                        onClick={handlePayAdvance}
                        className="shrink-0 rounded-lg bg-amber-600 px-3 py-2 text-xs font-semibold text-white hover:bg-amber-700 sm:text-sm"
                      >
                        Pay advance
                      </button>
                    ) : (
                      <span className="text-xs font-medium text-emerald-700">Paid ✓</span>
                    )}
                  </div>
                </div>
              )}

              <div className="hidden gap-3 border-t border-gray-100 p-4 sm:p-5 xl:flex">
                <button
                  type="button"
                  onClick={() => setStep(1)}
                  className="flex-1 rounded-lg border border-gray-300 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                >
                  Back
                </button>
                <button
                  type="button"
                  onClick={handleConfirm}
                  disabled={submitting || !canConfirm}
                  className="flex flex-[1.5] items-center justify-center gap-2 rounded-lg bg-red-600 py-2.5 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-50"
                >
                  {submitting ? <FaSpinner className="animate-spin" /> : null}
                  Confirm booking
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {typeof document !== 'undefined' &&
        createPortal(
          <div
            className="checkout-light-page-bar fixed inset-x-0 z-[60] border-t border-gray-200 bg-white px-4 py-3 shadow-[0_-4px_20px_rgba(0,0,0,0.08)] xl:hidden"
            style={{ bottom: 'calc(4.5rem + env(safe-area-inset-bottom, 0px))' }}
          >
            <div className="mx-auto w-full max-w-lg">
              {step === 1 ? (
                <>
                  {!canContinueStep1 && (
                    <p className="mb-2 text-center text-[11px] leading-snug text-gray-500">
                      Complete vehicle, service, issue (10+ chars), date &amp; time slot.
                    </p>
                  )}
                  <button
                    type="button"
                    onClick={handleContinue}
                    disabled={!canContinueStep1}
                    className="w-full rounded-lg bg-red-600 py-3 text-sm font-semibold text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Continue
                  </button>
                </>
              ) : (
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setStep(1)}
                    className="min-h-[2.75rem] flex-1 rounded-lg border border-gray-300 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                  >
                    Back
                  </button>
                  <button
                    type="button"
                    onClick={handleConfirm}
                    disabled={submitting || !canConfirm}
                    className="flex min-h-[2.75rem] flex-[1.5] items-center justify-center gap-2 rounded-lg bg-red-600 py-2.5 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-50"
                  >
                    {submitting ? <FaSpinner className="animate-spin" /> : null}
                    Confirm booking
                  </button>
                </div>
              )}
            </div>
          </div>,
          document.body
        )}

    </div>
  );
}
